/**
 * CloudProvidersPanel — Owner-only "Cloud Provider Status" tile.
 *
 * One-screenshot proof to enterprise prospects that ERIO is genuinely
 * cloud-portable: shows which provider is currently wired to each
 * abstraction layer (secrets / storage / telemetry). Defaults to
 * env / local / sentry today; flips to Azure Key Vault, AWS S3, or
 * any other supported backend purely via config — zero code change.
 *
 * Calls GET /api/admin/cloud-providers (Owner-gated).
 */
import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Cloud, KeyRound, HardDrive, Activity, Database, Building2, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

const LAYER_META = {
  secrets: {
    label: "Secrets",
    icon: KeyRound,
    blurb: "API keys, database credentials, OIDC client secrets.",
  },
  storage: {
    label: "Storage",
    icon: HardDrive,
    blurb: "Board pack PDFs, audit exports, generated artefacts.",
  },
  telemetry: {
    label: "Telemetry",
    icon: Activity,
    blurb: "Error tracking, performance spans, request tagging.",
  },
  tenant_db: {
    label: "Tenant database",
    icon: Database,
    blurb: "Routes each tenant to shared, dedicated DB, or dedicated cluster.",
  },
  customer_base: {
    label: "Customer base shape",
    icon: Building2,
    blurb: "Distribution of tenants by company size band (Owner-captured).",
  },
};

// Stable badge palette for each provider so the Owner can recognise it
// at a glance. `env` / `local` / `sentry` are the Emergent defaults;
// the cloud values appear once you flip the env var.
const PROVIDER_TONE = {
  env: "bg-ek-ink/5 text-ek-ink",
  local: "bg-ek-ink/5 text-ek-ink",
  sentry: "bg-violet-50 text-violet-700",
  azure_keyvault: "bg-sky-50 text-sky-700",
  azure_blob: "bg-sky-50 text-sky-700",
  aws_secrets_manager: "bg-amber-50 text-amber-700",
  aws_s3: "bg-amber-50 text-amber-700",
  shared: "bg-emerald-50 text-emerald-700",
  configured: "bg-violet-50 text-violet-700",
  vault: "bg-slate-100 text-slate-700",
};

export function CloudProvidersPanel() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await api.get("/admin/cloud-providers");
      setRows(r.data?.providers || []);
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load provider status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <section
      className="mt-6 rounded-2xl border border-black/5 bg-white p-6"
      data-testid="cloud-providers-panel"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <span className="mt-1 flex h-9 w-9 items-center justify-center rounded-full bg-violet-50 text-violet-700">
            <Cloud className="h-4 w-4" />
          </span>
          <div>
            <h2 className="font-display text-lg font-semibold text-ek-ink">
              Cloud portability — provider status
            </h2>
            <p className="mt-1 text-sm text-ek-ink/70">
              ERIO runs the same code on Emergent today and on Azure or AWS
              tomorrow. Each abstraction layer below routes through a single
              env-var pivot —{" "}
              <span className="font-medium text-ek-ink">
                no code change required
              </span>{" "}
              when a customer asks for their own tenancy.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="text-[11px] font-semibold text-ek-ink/60 hover:text-ek-purple disabled:opacity-50"
          data-testid="cloud-providers-refresh"
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {err && (
        <p
          className="mt-4 text-sm text-red-600"
          data-testid="cloud-providers-error"
        >
          {err}
        </p>
      )}

      {loading && !rows && (
        <p className="mt-4 inline-flex items-center gap-2 text-sm text-ek-ink/60">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </p>
      )}

      {rows && (
        <ul className="mt-5 space-y-3" data-testid="cloud-providers-list">
          {rows.map((row) => {
            const meta = LAYER_META[row.layer] || {
              label: row.layer,
              icon: Cloud,
              blurb: "",
            };
            const Icon = meta.icon;
            const tone = PROVIDER_TONE[row.provider] || "bg-ek-ink/5 text-ek-ink";
            return (
              <li
                key={row.layer}
                className="flex items-start justify-between gap-4 rounded-xl border border-black/5 bg-ek-ink/[0.02] p-3"
                data-testid={`cloud-providers-row-${row.layer}`}
              >
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-7 w-7 items-center justify-center rounded-full bg-white text-ek-ink/70 ring-1 ring-black/5">
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-ek-ink">
                      {meta.label}
                    </p>
                    <p className="text-xs text-ek-ink/60">{meta.blurb}</p>
                    <p
                      className="mt-1 text-xs text-ek-ink/70"
                      data-testid={`cloud-providers-detail-${row.layer}`}
                    >
                      {row.detail}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 whitespace-nowrap">
                  <span
                    className={`rounded-full px-2 py-0.5 text-[11px] font-mono font-semibold uppercase tracking-wide ${tone}`}
                    data-testid={`cloud-providers-badge-${row.layer}`}
                  >
                    {row.provider}
                  </span>
                  {row.healthy ? (
                    <CheckCircle2
                      className="h-4 w-4 text-emerald-600"
                      aria-label="Healthy"
                    />
                  ) : (
                    <AlertTriangle
                      className="h-4 w-4 text-amber-500"
                      aria-label="Attention required"
                    />
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

export default CloudProvidersPanel;
